/**
 * Template System List
 * 
 * Sortable list of systems with drag-and-drop reordering
 */

import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { GripVertical, Plus, Trash2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import type { TemplateSystem } from '@/hooks/useInspectionTemplateEditor';

interface TemplateSystemListProps {
  systems: TemplateSystem[];
  selectedSystemId: string | null;
  onSelectSystem: (id: string) => void;
  onAddSystem?: (data: { system_key: string; display_name: string }) => Promise<any>;
  onUpdateSystem?: (id: string, updates: { display_name?: string }) => Promise<void>;
  onDeleteSystem?: (id: string) => Promise<void>;
  onReorderSystems?: (orderedIds: string[]) => Promise<void>;
  disabled?: boolean;
  isLoading?: boolean;
}

interface SortableSystemProps {
  system: TemplateSystem;
  isSelected: boolean;
  onSelect: () => void;
  onDelete?: () => void;
  disabled?: boolean;
}

function SortableSystem({ system, isSelected, onSelect, onDelete, disabled }: SortableSystemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: system.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors',
        isSelected ? 'bg-primary/10 border-primary' : 'bg-card hover:bg-accent',
        isDragging && 'opacity-50',
        disabled && 'opacity-60'
      )}
      onClick={onSelect}
    >
      {!disabled && (
        <button
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <span className="flex-1 text-sm font-medium truncate">{system.display_name}</span>
      {system.is_required && (
        <span className="text-xs text-muted-foreground">Required</span>
      )}
      {!disabled && onDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

export function TemplateSystemList({
  systems,
  selectedSystemId,
  onSelectSystem,
  onAddSystem,
  onUpdateSystem,
  onDeleteSystem,
  onReorderSystems,
  disabled,
  isLoading,
}: TemplateSystemListProps) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newSystemName, setNewSystemName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && onReorderSystems) {
      const oldIndex = systems.findIndex((s) => s.id === active.id);
      const newIndex = systems.findIndex((s) => s.id === over.id);
      const newOrder = arrayMove(systems, oldIndex, newIndex);
      await onReorderSystems(newOrder.map((s) => s.id));
    }
  };

  const handleAddSystem = async () => {
    if (!onAddSystem || !newSystemName.trim()) return;
    
    setIsAdding(true);
    try {
      const key = newSystemName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      await onAddSystem({ system_key: key, display_name: newSystemName.trim() });
      setNewSystemName('');
      setAddDialogOpen(false);
    } finally {
      setIsAdding(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={systems.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {systems.map((system) => (
            <SortableSystem
              key={system.id}
              system={system}
              isSelected={system.id === selectedSystemId}
              onSelect={() => onSelectSystem(system.id)}
              onDelete={onDeleteSystem ? () => onDeleteSystem(system.id) : undefined}
              disabled={disabled}
            />
          ))}
        </SortableContext>
      </DndContext>

      {systems.length === 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground">
          No systems yet
        </div>
      )}

      {!disabled && onAddSystem && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setAddDialogOpen(true)}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add System
        </Button>
      )}

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add System</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="systemName">System Name</Label>
              <Input
                id="systemName"
                value={newSystemName}
                onChange={(e) => setNewSystemName(e.target.value)}
                placeholder="e.g., Brakes, Tires, Suspension"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSystem} disabled={isAdding || !newSystemName.trim()}>
              {isAdding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
